import React, { useState, useCallback } from 'react';

import styles from './DateInput.module.scss';

const CLOCK_SIZE = 210;
const CENTER = CLOCK_SIZE / 2;
const OUTER_RADIUS = 85;
const INNER_RADIUS = 58;
const SELECT_R = 16;
const EDGE_RADIUS = 99;

const OUTER_HOURS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const INNER_HOURS = [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const MINUTE_VALUES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

function toXY(value: number, total: number, radius: number) {
  const angle = ((value / total) * 360 - 90) * (Math.PI / 180);
  return { x: CENTER + radius * Math.cos(angle), y: CENTER + radius * Math.sin(angle) };
}

interface TimePanelProps {
  selectedTime: Date | null;
  onSelect: (time: Date) => void;
}

export const TimePanel: React.FC<TimePanelProps> = ({ selectedTime, onSelect }) => {
  const [mode, setMode] = useState<'hours' | 'minutes'>('hours');
  const hour = selectedTime ? selectedTime.getHours() : null;
  const minute = selectedTime ? selectedTime.getMinutes() : null;

  const buildDate = useCallback(
    (h: number, m: number) => {
      const d = selectedTime ? new Date(selectedTime) : new Date(2000, 0, 1);
      d.setHours(h, m, 0, 0);
      return d;
    },
    [selectedTime],
  );

  const onHour = useCallback(
    (h: number) => {
      onSelect(buildDate(h, minute ?? 0));
      setMode('minutes');
    },
    [minute, onSelect, buildDate],
  );

  const onMinute = useCallback(
    (m: number) => {
      onSelect(buildDate(hour ?? 0, m));
    },
    [hour, onSelect, buildDate],
  );

  const handTarget = (() => {
    if (mode === 'hours' && hour !== null) {
      return toXY(hour % 12, 12, hour >= 12 ? INNER_RADIUS : OUTER_RADIUS);
    }
    if (mode === 'minutes' && minute !== null) {
      return toXY(minute, 60, OUTER_RADIUS);
    }
    return null;
  })();

  const pad = (n: number) => String(n).padStart(2, '0');

  // Subtle edge tick dots (60 marks, every 5th larger)
  const ticks = Array.from({ length: 60 }, (_, i) => {
    const pos = toXY(i, 60, EDGE_RADIUS);
    return <circle key={i} cx={pos.x} cy={pos.y} r={i % 5 === 0 ? 1.5 : 0.7} className={styles.clockTick} />;
  });

  return (
    <div className={styles.clockPanel}>
      <div className={styles.clockHeader}>
        <button
          type="button"
          className={`${styles.clockHeaderDigit} ${mode === 'hours' ? styles.clockHeaderDigitActive : ''}`}
          onClick={() => setMode('hours')}
        >
          {hour !== null ? pad(hour) : '--'}
        </button>
        <span className={styles.clockHeaderColon}>:</span>
        <button
          type="button"
          className={`${styles.clockHeaderDigit} ${mode === 'minutes' ? styles.clockHeaderDigitActive : ''}`}
          onClick={() => setMode('minutes')}
        >
          {minute !== null ? pad(minute) : '--'}
        </button>
      </div>

      <div className={styles.clockModeLabel}>{mode === 'hours' ? 'часы' : 'минуты'}</div>

      <div className={styles.clockFace}>
        <svg width={CLOCK_SIZE} height={CLOCK_SIZE} className={styles.clockSvg}>
          {/* Background */}
          <circle cx={CENTER} cy={CENTER} r={CENTER - 2} className={styles.clockBg} />

          {/* Tick marks */}
          {ticks}

          {/* Clock hand */}
          {handTarget && (
            <>
              <line x1={CENTER} y1={CENTER} x2={handTarget.x} y2={handTarget.y} className={styles.clockHand} />
              <circle cx={CENTER} cy={CENTER} r={5} className={styles.clockDot} />
              <circle cx={handTarget.x} cy={handTarget.y} r={SELECT_R} className={styles.clockSelectCircle} />
            </>
          )}

          {/* Hour numbers */}
          {mode === 'hours' && (
            <>
              {OUTER_HOURS.map(h => {
                const p = toXY(h, 12, OUTER_RADIUS);
                const sel = hour === h;
                return (
                  <g key={h} onClick={() => onHour(h)} className={styles.clockNum} role="button">
                    <circle cx={p.x} cy={p.y} r={SELECT_R} className={styles.clockHitArea} />
                    <text x={p.x} y={p.y} className={`${styles.clockLabel} ${sel ? styles.clockLabelSelected : ''}`}>
                      {h === 0 ? '00' : String(h)}
                    </text>
                  </g>
                );
              })}
              {INNER_HOURS.map(h => {
                const p = toXY(h % 12, 12, INNER_RADIUS);
                const sel = hour === h;
                return (
                  <g key={h} onClick={() => onHour(h)} className={styles.clockNum} role="button">
                    <circle cx={p.x} cy={p.y} r={SELECT_R} className={styles.clockHitArea} />
                    <text
                      x={p.x}
                      y={p.y}
                      className={`${styles.clockLabel} ${styles.clockLabelInner} ${sel ? styles.clockLabelSelected : ''}`}
                    >
                      {String(h)}
                    </text>
                  </g>
                );
              })}
            </>
          )}

          {/* Minute numbers */}
          {mode === 'minutes' &&
            MINUTE_VALUES.map(m => {
              const p = toXY(m, 60, OUTER_RADIUS);
              const sel = minute !== null && Math.round(minute / 5) * 5 === m;
              return (
                <g key={m} onClick={() => onMinute(m)} className={styles.clockNum} role="button">
                  <circle cx={p.x} cy={p.y} r={SELECT_R} className={styles.clockHitArea} />
                  <text x={p.x} y={p.y} className={`${styles.clockLabel} ${sel ? styles.clockLabelSelected : ''}`}>
                    {pad(m)}
                  </text>
                </g>
              );
            })}
        </svg>
      </div>
    </div>
  );
};
